import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { type ExecuteAction } from '@mls-wallet/shared-types';

type ExecuteAdminChangeProposalArg = {
  proposalId: string;
  action: ExecuteAction;
};

/**
 * Response from execute endpoint.
 * Contains the guardianTransactionId for polling execution status.
 */
type ExecuteAdminChangeProposalResponse = {
  guardianTransactionId: string;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: ExecuteAdminChangeProposalArg }
): Promise<ExecuteAdminChangeProposalResponse> => {
  const { proposalId, action } = arg;
  return await denApi.post<
    ExecuteAdminChangeProposalResponse,
    { action: ExecuteAction }
  >(
    `admin/organization/admin-change-proposals/${encodeURIComponent(
      proposalId
    )}/execute`,
    { action }
  );
};

/**
 * Executes an admin change proposal (approve or reject).
 *
 * For APPROVE action:
 * - Can only be called once the proposal has received enough approval signatures.
 * - Returns a guardianTransactionId for async execution tracking via polling.
 *
 * For REJECT action:
 * - Can only be called once the proposal has received enough rejection signatures.
 * - Returns a guardianTransactionId for async execution tracking via polling.
 *
 * @param swrOptions - Optional SWR mutation configuration overrides
 * @returns executeProposal trigger function, loading state, and error
 */
export function useExecuteAdminChangeProposal(
  swrOptions?: SWRMutationConfiguration<
    ExecuteAdminChangeProposalResponse,
    Error,
    string,
    ExecuteAdminChangeProposalArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    ExecuteAdminChangeProposalResponse,
    Error,
    string,
    ExecuteAdminChangeProposalArg
  >('admin/organization/admin-change-proposals/execute', fetcher, swrOptions);

  return { executeProposal: trigger, isLoading: isMutating, error };
}
